/**
 * useEscapeKey.js - Hook pour fermer les menus avec la touche Échap
 * 
 * Hook personnalisé qui écoute la touche Escape et appelle un callback.
 * Réutilisable pour tous les menus, modales, popups, etc.
 * 
 * UTILISATION :
 * ```jsx
 * import { useEscapeKey } from '../hooks/useEscapeKey'
 * 
 * function MyMenu() {
 *   const [isOpen, setIsOpen] = useState(false)
 *   
 *   // Ferme le menu quand Escape est pressé
 *   useEscapeKey(() => {
 *     if (isOpen) {
 *       setIsOpen(false)
 *     }
 *   }, isOpen) // Le hook ne s'active que si isOpen = true
 *   
 *   return <div>...</div>
 * }
 * ```
 * 
 * AVANTAGES :
 * - Centralise la logique de gestion de la touche Escape
 * - Évite les répétitions de code
 * - Facile à maintenir
 * - Réutilisable dans tout le projet
 * 
 * @param {Function} callback - Fonction à appeler quand Escape est pressé
 * @param {boolean} isActive - Si true, le hook écoute les événements clavier
 */

import { useEffect } from 'react'

export function useEscapeKey(callback, isActive = true) {
  useEffect(() => {
    // Ne rien faire si le hook n'est pas actif
    if (!isActive) return

    /**
     * Gestionnaire d'événement clavier
     * @param {KeyboardEvent} event - Événement clavier
     */
    function handleKeyDown(event) {
      // Vérifie si la touche Escape a été pressée
      if (event.key === 'Escape' || event.keyCode === 27) {
        // Empêche le comportement par défaut (si existant)
        event.preventDefault()
        
        // Appelle le callback fourni
        callback()
      }
    }

    // Ajoute l'event listener au document
    // useCapture: true pour être prioritaire sur d'autres listeners
    document.addEventListener('keydown', handleKeyDown, true)

    // Cleanup : retire l'event listener quand le composant se démonte
    // ou quand les dépendances changent
    return () => {
      document.removeEventListener('keydown', handleKeyDown, true)
    }
  }, [callback, isActive])
}

/**
 * Hook alternatif avec gestion de plusieurs menus (priorité)
 * 
 * Si plusieurs menus sont ouverts, ferme seulement le dernier ouvert (le plus prioritaire).
 * Utilise un système de stack pour gérer l'ordre d'ouverture.
 * 
 * UTILISATION FUTURE (pour plusieurs menus) :
 * ```jsx
 * // Menu 1
 * useEscapeKey(() => setMenu1Open(false), menu1Open, 'menu1')
 * 
 * // Menu 2 (ouvert après Menu 1)
 * useEscapeKey(() => setMenu2Open(false), menu2Open, 'menu2')
 * 
 * // Si les deux sont ouverts et qu'on appuie sur Escape,
 * // seul Menu 2 se fermera (le dernier ouvert)
 * ```
 */

// Stack global pour gérer la priorité des menus
let menuStack = []

export function useEscapeKeyWithPriority(callback, isActive = true, menuId = 'default') {
  useEffect(() => {
    if (!isActive) {
      // Retire ce menu de la stack s'il n'est plus actif
      menuStack = menuStack.filter(id => id !== menuId)
      return
    }

    // Ajoute ce menu à la stack (devient prioritaire)
    if (!menuStack.includes(menuId)) {
      menuStack.push(menuId)
    }

    function handleKeyDown(event) {
      if (event.key === 'Escape' || event.keyCode === 27) {
        // Vérifie si ce menu est le dernier de la stack (le plus prioritaire)
        const lastMenuId = menuStack[menuStack.length - 1]
        
        if (lastMenuId === menuId) {
          event.preventDefault()
          callback()
          
          // Retire ce menu de la stack après fermeture
          menuStack = menuStack.filter(id => id !== menuId)
        }
      }
    }

    document.addEventListener('keydown', handleKeyDown, true)

    return () => {
      document.removeEventListener('keydown', handleKeyDown, true)
      menuStack = menuStack.filter(id => id !== menuId)
    }
  }, [callback, isActive, menuId])
}

/**
 * DOCUMENTATION TECHNIQUE
 * 
 * Pourquoi useCapture: true ?
 * - Les événements clavier peuvent être capturés à différentes phases (capture / bubble)
 * - useCapture: true permet d'intercepter l'événement AVANT qu'il atteigne les autres listeners
 * - Utile si d'autres composants écoutent aussi la touche Escape
 * 
 * Pourquoi event.preventDefault() ?
 * - Certains navigateurs ont des comportements par défaut pour Escape
 * - Par exemple, sortir du mode plein écran
 * - preventDefault() empêche ces comportements si on gère nous-mêmes la fermeture
 * 
 * Pourquoi le paramètre isActive ?
 * - Permet de contrôler quand le hook doit écouter
 * - Évite d'appeler le callback quand le menu est déjà fermé
 * - Optimise les performances en ne créant pas d'event listeners inutiles
 * 
 * Stack de priorité (useEscapeKeyWithPriority) :
 * - Résout le problème : "si j'ai 2 menus ouverts, lequel fermer ?"
 * - Le dernier menu ouvert a la priorité
 * - Les menus se ferment dans l'ordre inverse de leur ouverture (LIFO)
 * - Comportement UX standard (comme les modales)
 */

